import {callAPI} from '@/store/utils';

const FYGR_COMPANY = 'FYGR_COMPANY';

export default {
  namespaced: true,
  state: {
    // Server-driven guide steps (getStartOnboarding) + their "done" state.
    chapters: [],
    // Flags racine de l'onboarding (getStartOnboarding → result.onboarding) :
    //   - isStartGuide : le guide a-t-il déjà été démarré (le back le passe à
    //     true automatiquement dès qu'un chapitre est complété).
    //   - completed / dismissed : onboarding terminé / désactivé.
    guideMeta: {id: null, isStartGuide: false, completed: false, dismissed: false},
    completedChapter: '',
    // Chapters offered to a company at onboarding (availableChapters endpoint),
    // used to build the priority cards.
    companyOnboardingChapters: [],
  },
  mutations: {
    setChapters(state, chapters) {
      state.chapters =
        chapters.sort((a, b) => {
          if (a.order > b.order) return 1;
          if (a.order < b.order) return -1;
          return 0;
        }) || [];
    },
    markChapterCompleted(state, chapter) {
      const chapters = [...state.chapters];
      const chapterIdx = chapters.findIndex((c) => c.condition_key === chapter);
      if (chapterIdx !== -1) {
        chapters[chapterIdx].completed = true;
      } else if (chapter === 'guide') {
        chapters.push({condition_key: 'guide', completed: true});
      }
      state.chapters = [...chapters];
      if (
        state.chapters.length &&
        state.chapters.length ===
          state.chapters.filter((c) => c.completed).length
      ) {
        state.completedChapter = 'guide';
      }
    },
    setChapterCompleted(state, completedChapter) {
      state.completedChapter = completedChapter;
    },
    setCompanyOnboardingChapters(state, companyOnboardingChapters) {
      state.companyOnboardingChapters = companyOnboardingChapters;
    },
    setGuideMeta(state, meta) {
      state.guideMeta = {
        id: meta?.id ?? null,
        isStartGuide: meta?.isStartGuide ?? false,
        completed: meta?.completed ?? false,
        dismissed: meta?.dismissed ?? false,
      };
    },
  },
  actions: {
    async actionGetStartOnboarding({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getStartOnboarding', {companyId});
      // Nouveau shape : result.onboarding = { id, is_start_guide, completed,
      // dismissed, chapters:[…] }. Fallback sur l'ancien shape (result = tableau)
      // au cas où l'API ne serait pas encore à jour.
      const onboarding = res?.error ? null : res?.result?.onboarding ?? null;
      const chapters =
        onboarding?.chapters ?? (Array.isArray(res?.result) ? res.result : []);
      commit('setChapters', chapters);
      commit('setGuideMeta', {
        id: onboarding?.id ?? null,
        isStartGuide: onboarding?.is_start_guide ?? false,
        completed: onboarding?.completed ?? false,
        dismissed: onboarding?.dismissed ?? false,
      });
    },
    // Chapters offered to a company at onboarding (drives the priority cards).
    // Persists to state and returns the raw array; the UI mapping stays in the
    // component.
    async actionGetCompanyOnboardingChapters({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      if (!companyId) return [];
      try {
        const res = await callAPI(
          'GET',
          `company/${companyId}/availableChapters`,
        );
        // The array can arrive at the top level or nested under
        // result / result.chapters / chapters — pick whichever holds it.
        let chapters = [];
        if (res?.error) chapters = [];
        else if (Array.isArray(res)) chapters = res;
        else if (Array.isArray(res.result)) chapters = res.result;
        else if (Array.isArray(res.result?.chapters))
          chapters = res.result.chapters;
        else if (Array.isArray(res.chapters)) chapters = res.chapters;
        commit('setCompanyOnboardingChapters', chapters);
        return chapters;
      } catch (err) {
        console.warn('[availableChapters] fetch failed', err);
        commit('setCompanyOnboardingChapters', []);
        return [];
      }
    },
    // Upsert the chapters the user picked at onboarding (the priority cards).
    // Body: {chapters: [<chapterId>, ...]}.
    async actionUpsertCompanyOnboardingChapters(_, chapters = []) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `company/${companyId}/companyOnboardingChapters`,
        {chapters},
      );
      return res;
    },
    // Toggle the "don't suggest onboarding chapters again" choice.
    async actionDismissCompanyOnboardingChapters(_, {dismissed} = {}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `company/${companyId}/companyOnboardingChapters/dismiss`,
        {dismissed},
      );
      return res;
    },
    // Mark a single onboarding chapter as completed.
    async actionCompleteCompanyOnboardingChapter(_, chapterId) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `company/${companyId}/companyOnboardingChapter/${chapterId}/complete`,
      );
      return res;
    },
    actionShowChapterCompleted({commit}, chapter) {
      commit('setChapterCompleted', chapter);
    },
    actionSetChapterCompleted({commit}, chapter) {
      commit('markChapterCompleted', chapter);
    },
    async actionSetOnboardingDismissed({commit, state}, params = {}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const dismissed = params.dismissed ?? true;
      const res = await callAPI('POST', 'setOnboardingDismissed', {
        ...params,
        dismissed,
        companyId,
      });
      if (!res?.error) {
        // On garde les chapitres (pour la checklist) et on reflète le flag.
        commit('setGuideMeta', {...state.guideMeta, dismissed});
      }
      return res;
    },
  },
};
